import { useEffect, useState } from "react";
import { TopBar } from "../../components/TopBar";
import { Modal } from "../../components/Modal";
import { PageHeader } from "../../components/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectContext";
import { useToast } from "../../context/ToastContext";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../api/client";
import { BrandingEditor } from "../../components/BrandingEditor";
import type { Branding, Member } from "../../api/types";

export function Settings() {
  const { user, updateUser } = useAuth();
  const { active, updateProject } = useProjects();
  const pid = active?.id ?? "";
  const toast = useToast();
  const members = useAsync(() => api.listMembers(pid), [pid]);

  // --- project rename ---
  const [projName, setProjName] = useState(active?.name ?? "");
  const [projBusy, setProjBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  // Sync the input when the active project loads or the user switches projects.
  useEffect(() => {
    setProjName(active?.name ?? "");
  }, [active?.id, active?.name]);

  const nameDirty = !!active && projName.trim() !== "" && projName.trim() !== active.name;

  // --- project branding (Gap 1: default look for this project's public pages) ---
  const [brand, setBrand] = useState<Branding>(active?.branding ?? {});
  const [brandBusy, setBrandBusy] = useState(false);
  useEffect(() => {
    setBrand(active?.branding ?? {});
  }, [active?.id, active?.branding]);

  async function handleSaveBranding() {
    if (!active) return;
    setBrandBusy(true);
    try {
      await updateProject(active.id, { branding: brand });
      toast("Project branding saved");
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setBrandBusy(false);
    }
  }

  async function handleSaveProject() {
    if (!active || !nameDirty) return;
    setProjBusy(true);
    try {
      await updateProject(active.id, { name: projName.trim() });
      toast("Project renamed");
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setProjBusy(false);
    }
  }

  // --- profile name edit ---
  const [profileName, setProfileName] = useState(user?.name ?? "");
  const [profileBusy, setProfileBusy] = useState(false);

  useEffect(() => {
    setProfileName(user?.name ?? "");
  }, [user?.name]);

  const profileDirty = profileName.trim() !== "" && profileName.trim() !== user?.name;

  async function handleSaveProfile() {
    if (!profileDirty) return;
    setProfileBusy(true);
    try {
      const updated = await api.updateProfile(profileName.trim());
      updateUser(updated);
      toast("Profile name updated");
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setProfileBusy(false);
    }
  }

  // --- change password modal ---
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  function resetPwModal() {
    setCurPw(""); setNewPw(""); setConfirmPw("");
    setChangePwOpen(false);
  }

  async function handleChangePassword() {
    if (newPw.length < 8) {
      toast("New password must be at least 8 characters", "err");
      return;
    }
    if (newPw !== confirmPw) {
      toast("New passwords don't match", "err");
      return;
    }
    setPwBusy(true);
    try {
      await api.changePassword(curPw, newPw);
      toast("Password changed");
      resetPwModal();
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setPwBusy(false);
    }
  }

  // --- resend verification ---
  async function handleResendVerification() {
    setResendBusy(true);
    try {
      await api.resendVerification();
      toast("Verification email sent — check your inbox");
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setResendBusy(false);
    }
  }

  // --- invite member modal ---
  const activeAccountId = api.getActiveAccountId();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Member["role"]>("member");
  const [inviteBusy, setInviteBusy] = useState(false);

  function resetInviteModal() {
    setInviteEmail(""); setInviteRole("member"); setInviteOpen(false);
  }

  async function handleInvite() {
    if (!activeAccountId) { toast("No active account", "err"); return; }
    if (!inviteEmail.trim().includes("@")) { toast("Enter a valid email", "err"); return; }
    setInviteBusy(true);
    try {
      await api.inviteMember(activeAccountId, inviteEmail.trim(), inviteRole);
      toast("Member added");
      resetInviteModal();
      members.reload();
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setInviteBusy(false);
    }
  }

  return (
    <>
      <TopBar crumbs={<b>Settings</b>} />
      <div className="content">
        <PageHeader title="Settings" intent="Account, project, and team management." />

        {/* Account */}
        <section className="card form-section">
          <div className="sec-title"><h3>Account</h3></div>
          <p className="sec-desc">Your personal profile.</p>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="profile-name">Name</label>
              <input
                id="profile-name"
                className="input"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                disabled={profileBusy}
              />
            </div>
            <div className="field"><label>Email</label><input className="input" defaultValue={user?.email} disabled /></div>
          </div>
          <div className="row" style={{ marginTop: 18, gap: 10, alignItems: "center" }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSaveProfile}
              disabled={!profileDirty || profileBusy}
            >
              {profileBusy ? <span className="spinner" /> : "Save profile"}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setChangePwOpen(true)}
            >
              Change password
            </button>
            {user && !user.email_verified && (
              <>
                <span className="tag tag-coral">email not verified</span>
                <button className="btn btn-ghost btn-sm" onClick={handleResendVerification} disabled={resendBusy}>
                  {resendBusy ? <span className="spinner" /> : "Resend verification"}
                </button>
              </>
            )}
            {user?.email_verified && <span className="tag tag-dim">email verified</span>}
          </div>
        </section>

        {/* Project */}
        <section className="card form-section">
          <div className="sec-title"><h3>Project</h3></div>
          <p className="sec-desc">Settings for the active project, <b>{active?.name}</b>.</p>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="proj-name">Project name</label>
              <input
                id="proj-name"
                className="input"
                value={projName}
                onChange={(e) => setProjName(e.target.value)}
                disabled={!active || projBusy}
              />
            </div>
          </div>
          <div className="row" style={{ marginTop: 18, gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={handleSaveProject} disabled={!nameDirty || projBusy}>
              {projBusy ? <span className="spinner" /> : "Save project"}
            </button>
            {active?.slug && (
              <span className="faint" style={{ fontSize: 12.5 }}>
                API namespace:{" "}
                <button
                  className="api-key-chip"
                  title="Copy this project's API namespace"
                  onClick={() =>
                    navigator.clipboard?.writeText(active.slug).then(() => toast("API namespace copied"))
                  }
                >
                  {active.slug}
                </button>
              </span>
            )}
          </div>
        </section>

        {/* Public page branding (Gap 1) — project-level default for every public board page. */}
        <section className="card form-section">
          <div className="sec-title"><h3>Public page branding</h3></div>
          <p className="sec-desc">
            The <b>default</b> look for every public leaderboard page in this project. Individual
            boards can override any of these in their own editor. Leave a field blank to use the
            built-in Laddr theme.
          </p>
          <BrandingEditor value={brand} onChange={setBrand} />
          <div className="row" style={{ marginTop: 18 }}>
            <button className="btn btn-primary" onClick={handleSaveBranding} disabled={!active || brandBusy}>
              {brandBusy ? <span className="spinner" /> : "Save branding"}
            </button>
          </div>
        </section>

        {/* Members */}
        <section className="card form-section">
          <div className="between" style={{ marginBottom: 16 }}>
            <div className="sec-title" style={{ marginBottom: 0 }}><h3>Team members</h3></div>
            <button className="btn btn-ghost btn-sm" onClick={() => setInviteOpen(true)}>＋ Invite member</button>
          </div>
          {members.loading ? (
            <div className="loading-rows">{[0, 1].map((i) => <div key={i} className="skel" />)}</div>
          ) : (members.data ?? []).length === 0 ? (
            <p className="muted" style={{ padding: "4px 2px" }}>
              You're the only member.
            </p>
          ) : (
            <table className="tbl">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th></tr></thead>
              <tbody>
                {(members.data ?? []).map((m) => (
                  <tr key={m.user_id}>
                    <td>{m.name}{m.user_id === user?.id && <span className="faint"> (you)</span>}</td>
                    <td className="faint">{m.email}</td>
                    <td><span className="tag tag-dim">{m.role}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {/* Change password modal */}
      {changePwOpen && (
        <Modal title="Change password" onClose={resetPwModal}>
          <div className="form-grid" style={{ marginTop: 16 }}>
            <div className="field">
              <label>Current password</label>
              <input
                type="password"
                className="input"
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                autoFocus
              />
            </div>
            <div className="field">
              <label>New password</label>
              <input
                type="password"
                className="input"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Confirm new password</label>
              <input
                type="password"
                className="input"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
              />
            </div>
          </div>
          <div className="row" style={{ marginTop: 20, justifyContent: "flex-end", gap: 10 }}>
            <button className="btn btn-ghost" onClick={resetPwModal} disabled={pwBusy}>Cancel</button>
            <button className="btn btn-primary" onClick={handleChangePassword} disabled={pwBusy || !curPw || !newPw || !confirmPw}>
              {pwBusy ? <span className="spinner" /> : "Change password"}
            </button>
          </div>
        </Modal>
      )}

      {/* Invite member modal */}
      {inviteOpen && (
        <Modal title="Invite team member" onClose={resetInviteModal}>
          <p className="muted" style={{ marginTop: 8 }}>
            The person must already have a Laddr account with this email address.
          </p>
          <div className="form-grid" style={{ marginTop: 16 }}>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                className="input"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@example.com"
                autoFocus
              />
            </div>
            <div className="field">
              <label>Role</label>
              <select
                className="input"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Member["role"])}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
          </div>
          <div className="row" style={{ marginTop: 20, justifyContent: "flex-end", gap: 10 }}>
            <button className="btn btn-ghost" onClick={resetInviteModal} disabled={inviteBusy}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleInvite}
              disabled={inviteBusy || !inviteEmail.trim()}
            >
              {inviteBusy ? <span className="spinner" /> : "Add member"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
